import { Radio } from '@/ui/radio-button/radio-button.component';
import { Checkbox } from '@/ui/checkbox/checkbox.component';
import React, { ComponentType, useMemo } from 'react';
import styles from '../duplicate-resolver.module.scss';
import { MergeCandidate, MergeState } from '../types';
import { getAttributesByType } from './attributes';
import { RelatedUserType } from './utils';

interface AttributesViewProps {
  candidates: MergeCandidate[];
  mergeState: MergeState;
  activeRecordIds: string[];
  primaryRecordId?: string;
  userType?: RelatedUserType;
  onStateChange: (property: string, personId: string) => void;
  contactPropsState: Record<string, boolean>;
  onContactPropToggle: (key: string, value: string, checked: boolean) => void;
}

export const AttributesView: ComponentType<AttributesViewProps> = (props) => {
  const {
    candidates,
    mergeState,
    userType,
    activeRecordIds,
    primaryRecordId,
    onStateChange,
    contactPropsState,
    onContactPropToggle,
  } = props;

  const attributes = useMemo(() => getAttributesByType(userType), [userType]);

  return (
    <>
      {attributes.map((item, idx) => {
        // --- Multi-value rows (email, phone) ---
        if (item.isMultiValue && item.getValues) {
          const allValues = [
            ...new Map(
              candidates.flatMap((c) =>
                (item.getValues!(c) ?? []).map((v) => [v.value, v]),
              ),
            ).values(),
          ];

          if (allValues.length === 0) {
            return (
              <tr key={idx}>
                <td>{item.label}</td>
                <td className={styles.masterCell}>–</td>
                {candidates.map((c) => (
                  <td key={c.id} />
                ))}
              </tr>
            );
          }

          return allValues.map((val, vidx) => {
            const stateKey = `${val.key}:${val.value}`;
            const included = contactPropsState[stateKey] ?? true;
            return (
              <tr key={`${idx}-${vidx}`}>
                {vidx === 0 && (
                  <td rowSpan={allValues.length}>{item.label}</td>
                )}
                <td className={styles.masterCell}>
                  <Checkbox
                    label={val.value}
                    checked={included}
                    onChange={(v) => onContactPropToggle(val.key, val.value, v)}
                    noSpacing
                    small
                  />
                </td>
                {candidates.map((candidate) => {
                  const has = (item.getValues!(candidate) ?? []).some(
                    (v) => v.value === val.value,
                  );
                  return (
                    <td
                      key={candidate.id}
                      className={
                        activeRecordIds.includes(candidate.id)
                          ? undefined
                          : styles.disabledCell
                      }
                    >
                      {has ? val.value : ''}
                    </td>
                  );
                })}
              </tr>
            );
          });
        }

        // --- Single-value radio row ---
        const propertyKey = item.name as string;
        const selectedId = mergeState[propertyKey] ?? primaryRecordId;

        return (
          <tr key={idx}>
            <td>{item.label}</td>
            <td className={styles.masterCell}>
              {item.render
                ? item.render(
                    candidates.find((c) => c.id === selectedId) ?? candidates[0],
                  )
                : '–'}
            </td>
            {candidates.map((record) => (
              <td
                key={record.id}
                className={
                  activeRecordIds.includes(record.id)
                    ? undefined
                    : styles.disabledCell
                }
              >
                <Radio
                  label={item.render ? (item.render(record) as string) : ''}
                  checked={selectedId === record.id}
                  onChange={() => onStateChange(propertyKey, record.id)}
                  disabled={!activeRecordIds.includes(record.id)}
                  value={record.id}
                  small
                />
              </td>
            ))}
          </tr>
        );
      })}
    </>
  );
};
